import { Box, Button, Dialog, Input, Stack, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import PhotoChoiceModal from './PhotoChoiceModal.jsx';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const calculateInterest = ({ amount, rate, startDate, endDate }) => {
  if (amount === null || amount === undefined || amount === '') return null;
  if (rate === null || rate === undefined || rate === '') return null;
  if (!startDate || !endDate) return null;
  const principal = Number(amount);
  const interestRate = Number(rate);
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(principal) || Number.isNaN(interestRate)) return null;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diffDays = Math.max(0, Math.ceil((end - start) / MS_PER_DAY));
  const months = diffDays / 30;
  const years = months / 12;
  const interest = principal * (interestRate / 100) * years;
  return Math.round(interest * 100) / 100;
};

const ReleaseModal = ({ isOpen, onClose, article, onSubmit }) => {
  const [releaseDate, setReleaseDate] = useState('');
  const [releaseAmount, setReleaseAmount] = useState(''); // New State for Received Amount
  const [isChoosing, setIsChoosing] = useState(false);
  const [fileName, setFileName] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedFile, setCapturedFile] = useState(null);
  const uploadRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const interestAmount = useMemo(() => calculateInterest({
    amount: article?.totalPrincipal || article?.amount, // Use updated total
    rate: article?.interestRate,
    startDate: article?.date,
    endDate: releaseDate
  }), [article, releaseDate]);

  // Pre-fill amount with (Principal + Interest) if date selected
  useEffect(() => {
    if (interestAmount !== null && article) {
      const principal = article.totalPrincipal || article.amount || 0;
      setReleaseAmount((principal + interestAmount).toFixed(0));
    }
  }, [interestAmount, article]);

  const resetState = () => {
    setReleaseDate('');
    setReleaseAmount('');
    setFileName('');
    if (uploadRef.current) uploadRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUpload = () => {
    if (cameraRef.current) cameraRef.current.value = '';
    setIsChoosing(false);
    setCameraError('');
    setCapturedFile(null);
    uploadRef.current?.click();
  };

  const handleCamera = async () => {
    if (uploadRef.current) uploadRef.current.value = '';
    setIsChoosing(false);
    setCameraError('');
    setCapturedFile(null);
    if (!navigator?.mediaDevices?.enumerateDevices) {
      setCameraError('Camera access is not supported on this device.');
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some((device) => device.kind === 'videoinput');
      if (!hasCamera) {
        setCameraError('No camera detected on this device.');
        return;
      }
    } catch (err) {
      setCameraError('Unable to access camera devices.');
      return;
    }
    setCameraOpen(true);
  };

  const handleFileChange = (input) => {
    const file = input.files?.[0];
    setFileName(file ? file.name : '');
    setCapturedFile(null);
  };

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedFile(file);
      setFileName(file.name);
      if (cameraRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        cameraRef.current.files = dataTransfer.files;
      }
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  const handleSubmit = async () => {
    const file = capturedFile
      || cameraRef.current?.files?.[0]
      || uploadRef.current?.files?.[0]
      || null;
    // Pass releaseAmount
    await onSubmit({ releaseDate, releaseAmount, file });
    handleClose();
  };

  useEffect(() => {
    const startCamera = async () => {
      if (!cameraOpen) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        setCameraError('Camera permission denied or unavailable.');
        setCameraOpen(false);
      }
    };
    startCamera();
    return () => {
      stopCameraStream();
    };
  }, [cameraOpen]);

  return (
    <>
      <Dialog.Root
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) handleClose();
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content w={{ base: '92%', sm: '480px' }} maxH="85vh" overflowY="auto">
            <Dialog.Header>
              <Dialog.Title>Release Article</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>Release Date</Text>
                  <Input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Received Amount (Final Settlement)</Text>
                  <Input 
                    type="number" 
                    value={releaseAmount} 
                    onChange={(e) => setReleaseAmount(e.target.value)} 
                    placeholder="Enter total amount collected"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>Release Photo</Text>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
                    <Button variant="outline" onClick={() => setIsChoosing(true)} w={{ base: 'full', md: 'auto' }}>
                      Choose Upload / Camera
                    </Button>
                    <Text fontSize="sm" color="gray.500" alignSelf={{ base: 'flex-start', md: 'center' }}>
                      {fileName || 'No file selected'}
                    </Text>
                  </Stack>
                  <Input ref={uploadRef} type="file" accept="image/*" hidden onChange={() => handleFileChange(uploadRef.current)} />
                  <Input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={() => handleFileChange(cameraRef.current)} />
                  {cameraError && (
                    <Text fontSize="sm" color="red.500" mt={2}>
                      {cameraError}
                    </Text>
                  )}
                </Box>
                <Text fontSize="sm" color="gray.600">
                  Calculated Interest: <strong>₹{interestAmount ? interestAmount.toFixed(2) : '0.00'}</strong>
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Client: {article?.client?.name || 'N/A'} | Article: {article?.nameOrNo || 'N/A'}
                </Text>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={{ base: 0, md: 3 }} onClick={handleClose} w={{ base: 'full', md: 'auto' }}>
                Cancel
              </Button>
              <Button colorScheme="green" onClick={handleSubmit} isDisabled={!releaseDate || !releaseAmount} w={{ base: 'full', md: 'auto' }}>
                Release
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <PhotoChoiceModal
        isOpen={isChoosing}
        onClose={() => setIsChoosing(false)}
        onUpload={handleUpload}
        onCamera={handleCamera}
      />

      {cameraOpen && (
        <Box position="fixed" inset="0" zIndex="1500">
          <Box
            position="absolute"
            inset="0"
            bg="blackAlpha.700"
            onClick={closeCamera}
          />
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg="white"
            w={{ base: '92%', sm: '420px' }}
            borderRadius="md"
            p={{ base: 3, sm: 4 }}
            boxShadow="lg"
          >
            <Text fontSize="lg" fontWeight="semibold" mb={3}>
              Camera
            </Text>
            <Box
              as="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              w="full"
              borderRadius="md"
              bg="black"
              maxH={{ base: '50vh', sm: '60vh' }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} mt={4} justify="flex-end">
              <Button variant="ghost" onClick={closeCamera} w={{ base: 'full', sm: 'auto' }}>Cancel</Button>
              <Button colorScheme="blue" onClick={capturePhoto} w={{ base: 'full', sm: 'auto' }}>Take Photo</Button>
            </Stack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ReleaseModal;
